import React, { useState, useEffect, useRef } from 'react';
import Icon from 'components/AppIcon';
import { searchAirports, formatAirportDisplay } from 'utils/airportData';

const AirportAutocomplete = ({ 
  value, 
  onChange, 
  placeholder, 
  label,
  icon = "MapPin",
  className = "",
  disabled = false 
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchResults, setSearchResults] = useState([]);
  const [inputValue, setInputValue] = useState(value || '');
  const [selectedIndex, setSelectedIndex] = useState(-1);
  
  const inputRef = useRef(null);
  const dropdownRef = useRef(null);

  useEffect(() => {
    setInputValue(value || '');
  }, [value]);

  useEffect(() => {
    if (inputValue?.length >= 2) {
      const results = searchAirports(inputValue, 8);
      setSearchResults(results);
      setIsOpen(results?.length > 0);
      setSelectedIndex(-1);
    } else {
      setSearchResults([]);
      setIsOpen(false);
      setSelectedIndex(-1);
    }
  }, [inputValue]);

  const handleInputChange = (e) => {
    const newValue = e?.target?.value;
    setInputValue(newValue);
    onChange?.(newValue);
  };

  const handleSelectAirport = (airport) => {
    const formatted = formatAirportDisplay(airport);
    setInputValue(formatted);
    onChange?.(formatted);
    setIsOpen(false);
    setSelectedIndex(-1);
    inputRef?.current?.blur();
  };

  const handleKeyDown = (e) => {
    if (!isOpen || searchResults?.length === 0) return;

    switch (e?.key) {
      case 'ArrowDown':
        e?.preventDefault();
        setSelectedIndex(prev => 
          prev < searchResults?.length - 1 ? prev + 1 : 0
        );
        break;
      case 'ArrowUp':
        e?.preventDefault();
        setSelectedIndex(prev => 
          prev > 0 ? prev - 1 : searchResults?.length - 1
        );
        break;
      case 'Enter':
        e?.preventDefault();
        if (selectedIndex >= 0 && searchResults?.[selectedIndex]) {
          handleSelectAirport(searchResults?.[selectedIndex]);
        }
        break;
      case 'Escape':
        setIsOpen(false);
        setSelectedIndex(-1);
        inputRef?.current?.blur();
        break;
      default:
        break;
    }
  };

  const handleFocus = () => {
    if (inputValue?.length >= 2 && searchResults?.length > 0) {
      setIsOpen(true);
    }
  };

  const handleBlur = (e) => {
    // Delay closing to allow for click on dropdown items
    setTimeout(() => {
      if (!dropdownRef?.current?.contains(e?.relatedTarget)) {
        setIsOpen(false);
        setSelectedIndex(-1);
      }
    }, 150);
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        inputRef?.current && 
        !inputRef?.current?.contains(event?.target) &&
        dropdownRef?.current && 
        !dropdownRef?.current?.contains(event?.target)
      ) {
        setIsOpen(false);
        setSelectedIndex(-1);
      }
    };

    document?.addEventListener('mousedown', handleClickOutside);
    return () => document?.removeEventListener('mousedown', handleClickOutside);
  }, []);

  return (
    <div className={`relative ${className}`}>
      {label && (
        <label className="block text-sm font-body-medium text-text-primary mb-2">
          {label}
        </label>
      )}
      
      <div className="relative">
        <input
          ref={inputRef}
          type="text"
          value={inputValue}
          onChange={handleInputChange}
          onKeyDown={handleKeyDown}
          onFocus={handleFocus}
          onBlur={handleBlur}
          placeholder={placeholder}
          disabled={disabled}
          className={`w-full pl-10 pr-4 py-3 border border-border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-all duration-200 ease-out ${
            disabled ? 'bg-secondary-50 cursor-not-allowed' : 'bg-white'
          }`}
          autoComplete="off"
        />
        
        <Icon 
          name={icon} 
          size={20} 
          className="absolute left-3 top-3.5 text-text-secondary" 
        />
        
        {isOpen && inputValue?.length >= 2 && (
          <Icon 
            name="ChevronDown" 
            size={16} 
            className="absolute right-3 top-4 text-text-secondary" 
          />
        )}
      </div>

      {/* Dropdown Results */}
      {isOpen && searchResults?.length > 0 && (
        <div 
          ref={dropdownRef}
          className="absolute z-50 w-full mt-1 bg-white border border-border rounded-lg shadow-lg max-h-64 overflow-y-auto"
        >
          {searchResults?.map((airport, index) => (
            <button
              key={airport?.iata}
              type="button"
              onClick={() => handleSelectAirport(airport)}
              className={`w-full px-4 py-3 text-left hover:bg-secondary-50 focus:bg-secondary-50 focus:outline-none transition-colors duration-150 ${
                index === selectedIndex ? 'bg-secondary-100' : ''
              } ${index === 0 ? 'rounded-t-lg' : ''} ${
                index === searchResults?.length - 1 ? 'rounded-b-lg' : 'border-b border-border'
              }`}
            >
              <div className="flex items-center justify-between">
                <div className="flex-1">
                  <div className="flex items-center space-x-2">
                    <span className="font-body-medium text-primary text-lg">
                      {airport?.iata}
                    </span>
                    <span className="text-text-primary font-body-medium">
                      {airport?.city}
                    </span>
                  </div>
                  <div className="text-sm text-text-secondary mt-1">
                    {airport?.name}
                  </div>
                  <div className="text-xs text-text-muted mt-0.5">
                    {airport?.country} • {airport?.region}
                  </div>
                </div>
                <Icon name="Plane" size={16} className="text-text-secondary flex-shrink-0" />
              </div>
            </button>
          ))}
        </div>
      )}

      {/* No Results Message */}
      {isOpen && inputValue?.length >= 2 && searchResults?.length === 0 && (
        <div className="absolute z-50 w-full mt-1 bg-white border border-border rounded-lg shadow-lg p-4">
          <div className="flex items-center space-x-2 text-text-secondary">
            <Icon name="Search" size={16} />
            <span className="text-sm">No airports found for "{inputValue}"</span>
          </div>
        </div>
      )}
    </div>
  );
};

export default AirportAutocomplete;